import { skyLeviathanNavigationBeat } from './93-sky-leviathan.mjs';
import { SKY_LEVIATHAN_SPACE as SPACE } from './86-sky-world-scale.mjs';
import { SKY_LEVIATHAN_ASSEMBLY as assembly } from './98-sky-leviathan-assembly.mjs';
export {
  SKY_LEVIATHAN_ASSEMBLY,
  skyLeviathanRupture,
  skyLeviathanRuptureSweep,
} from './98-sky-leviathan-assembly.mjs';
import {
  skyFlightFrameFromTangent,
  skyFlightPointToWorld,
  skyFlightVectorToWorld,
  skyFlightPointToLocal,
} from './92-sky-flight-frame.mjs';

const p = (x, y, z) => ({ x, y, z });
const portal = (center, halfWidth, halfHeight, normal = p(0, 0, -1), right = p(1, 0, 0)) => ({
  center,
  normal,
  right,
  up: p(0, 1, 0),
  halfWidth,
  halfHeight,
});

/** Measured mouth lining: rows are local Z, halfwidth, halfheight,
 * superellipse exponent, centreY and lower-jaw weight. The actual sculpted
 * lips own the front profile; the recessed machined threshold stays fixed. */
export const SKY_LEVIATHAN_VESTIBULE = Object.freeze(
  [
    [29.2, 8.6, 6.4, 2, -1.9, 1],
    [28.5, 7.4, 5.7, 2.8, -1.4, 0.6],
    [27.3, 4.6, 4, 3.4, 0, 0.15],
    [27, 3 + 3 / 7, 2.5 + 3 / 7, 4, 0, 0],
  ].map((row) => Object.freeze(row)),
);
export const SKY_LEVIATHAN_JAW = Object.freeze({
  pivot: Object.freeze(p(0, -2.5, 15.5)),
  openRadians: (42 * Math.PI) / 180,
});

// Two35mm bronze ridges are real surface relief, not decorative solids inside
// an unrelated collision shell. Their80mm shoulders interpolate measured rows.
export const SKY_LEVIATHAN_VESTIBULE_RINGS = Object.freeze(
  SKY_LEVIATHAN_VESTIBULE.flatMap((row, index, rows) => {
    if (index === 0 || index === rows.length - 1) return [row];
    const shoulder = (neighbor, offset) => {
      const t = offset / (neighbor[0] - row[0]);
      return Object.freeze(row.map((value, axis) => value + (neighbor[axis] - value) * t));
    };
    return [
      shoulder(rows[index - 1], 0.08),
      Object.freeze(row.map((value, axis) => value - (axis === 1 || axis === 2 ? 0.035 : 0))),
      shoulder(rows[index + 1], -0.08),
    ];
  }),
);

function vestibuleFacets(jaw) {
  const delta = (jaw - 1) * SKY_LEVIATHAN_JAW.openRadians;
  const cosine = Math.cos(delta),
    sine = Math.sin(delta),
    pivot = SKY_LEVIATHAN_JAW.pivot;
  const rings = SKY_LEVIATHAN_VESTIBULE_RINGS.map(
    ([z, width, height, exponent, centerY, jawWeight]) =>
      Array.from({ length: 32 }, (_, index) => {
        const angle = (index * Math.PI) / 16,
          x = Math.cos(angle),
          y = Math.sin(angle);
        const point = p(
          width * Math.sign(x) * Math.abs(x) ** (2 / exponent),
          centerY + height * Math.sign(y) * Math.abs(y) ** (2 / exponent),
          z,
        );
        const weight = Math.max(0, -y) * jawWeight;
        const localY = point.y - pivot.y,
          localZ = point.z - pivot.z;
        point.y += (pivot.y + cosine * localY - sine * localZ - point.y) * weight;
        point.z += (pivot.z + sine * localY + cosine * localZ - point.z) * weight;
        return Object.freeze(point);
      }),
  );
  return Object.freeze(
    rings.slice(1).flatMap((ring, row) =>
      ring.flatMap((point, column) => {
        const next = (column + 1) % 32,
          previous = rings[row];
        return [
          Object.freeze({
            id: `vestibule-${row}-${column}-a`,
            a: previous[column],
            b: previous[next],
            c: point,
          }),
          Object.freeze({
            id: `vestibule-${row}-${column}-b`,
            a: previous[next],
            b: ring[next],
            c: point,
          }),
        ];
      }),
    ),
  );
}
const openVestibule = vestibuleFacets(1),
  closedVestibule = vestibuleFacets(0);
let lastJaw = null,
  lastVestibule = null;
/** Exact visible lining facets follow the physical jaw hinge. Default is the
 * fully open fitting pose. Entry is held outside until jaw1; closure occurs
 * only after the pilot exits. Repeated/paused samples reuse immutable arrays. */
export function skyLeviathanVestibule(systems) {
  const jaw =
    systems === undefined
      ? 1
      : Math.max(0, Math.min(1, Number.isFinite(systems.jaw) ? systems.jaw : 0));
  if (jaw === 1) return openVestibule;
  if (jaw === 0) return closedVestibule;
  if (jaw !== lastJaw) {
    lastJaw = jaw;
    lastVestibule = vestibuleFacets(jaw);
  }
  return lastVestibule;
}

const vestibuleBounds = new WeakMap();
/** Conservative acquisition bounds measured from the exact current facets,
 * in hull-local metres. Ray contact still uses those facets, never this sphere. */
export function skyLeviathanVestibuleBounds(systems) {
  const triangles = skyLeviathanVestibule(systems);
  if (vestibuleBounds.has(triangles)) return vestibuleBounds.get(triangles);
  const points = triangles.flatMap(({ a, b, c }) => [a, b, c]);
  const center = Object.fromEntries(
    ['x', 'y', 'z'].map((axis) => [
      axis,
      (Math.min(...points.map((point) => point[axis])) +
        Math.max(...points.map((point) => point[axis]))) /
        2,
    ]),
  );
  const radius = Math.max(
    ...points.map((point) =>
      Math.hypot(point.x - center.x, point.y - center.y, point.z - center.z),
    ),
  );
  const bounds = Object.freeze({ center: Object.freeze(center), radius });
  vestibuleBounds.set(triangles, bounds);
  return bounds;
}

/** Canonical gameplay assembly, in hull-local metres. The hollow blockout owns
 * portals; target mounts below are authored mechanical sockets that final art
 * must fit. They are not claimed to be measured from a generated outer mesh. */
export function skyLeviathanGeometry() {
  const [mouthZ, mouthHalfWidth, mouthHalfHeight] = SKY_LEVIATHAN_VESTIBULE.at(-1);
  const component = (id, kind, center, hp, radius, extra = {}) => ({
    id,
    kind,
    center,
    hp,
    radius,
    ...extra,
  });
  return {
    pilotRadius: SPACE.pilotEnvelopeRadius,
    mouth: portal(p(0, 0, mouthZ), mouthHalfWidth, mouthHalfHeight),
    chamber: portal(p(0, 0, SPACE.chamberFrontZ), SPACE.throatWidth / 2, SPACE.throatHeight / 2),
    breach: portal(
      p(SPACE.breachX, 0, 0),
      SPACE.breachWidth / 2,
      SPACE.breachHeight / 2,
      p(1, 0, 0),
      p(0, 0, -1),
    ),
    components: [
      ...[-1, 1].flatMap((side) =>
        [0, 1].map((row) =>
          component(
            `nav-${side < 0 ? 'port' : 'starboard'}-${row}`,
            'navigation',
            p(side * 9, row ? -1.5 : 2.5, 36.3),
            6,
            0.28,
            { motionRadius: 9 },
          ),
        ),
      ),
      component('pressure-core', 'chamber', p(0, 0, -2), 24, 0.75),
      component('rib-upper', 'rib', p(4.1, 1.7, 0), 8, 0.24),
      component('rib-lower', 'rib', p(4.1, -1.7, 0), 8, 0.24),
      component('organ', 'organ', p(1.6, -2, -1), 14, 0.75, {
        requires: ['rib-upper', 'rib-lower'],
      }),
      component('fin-port', 'fin', p(-2.5, -2.4, -1.5), 10, 0.45, {
        side: 'port',
        requires: ['organ'],
        motionRadius: 0.8,
      }),
      component('fin-starboard', 'fin', p(2.5, -2.4, 1.5), 10, 0.45, {
        side: 'starboard',
        requires: ['organ'],
        motionRadius: 0.8,
      }),
    ],
    crashProbes: assembly.crash.supports.map(({ x, y, z }) => p(x, y, z)),
  };
}

/** Actual enterable route. Arc length s is metres, with a4m quarter-circle
 * turn from throat into the side breach. Values before/after the route extend
 * its end tangents so camera history and continuous handoffs remain defined. */
export function skyLeviathanInteriorPath(s) {
  const bend = 34,
    end = bend + 2 * Math.PI;
  if (s <= bend) return { position: p(0, 0, 38 - s), tangent: p(0, 0, -1) };
  if (s < end) {
    const angle = (s - bend) / SPACE.routeBendRadius;
    return {
      position: p(4 - 4 * Math.cos(angle), 0, 4 - 4 * Math.sin(angle)),
      tangent: p(Math.sin(angle), 0, -Math.cos(angle)),
    };
  }
  return { position: p(4 + s - end, 0, 0), tangent: p(1, 0, 0) };
}

/** Build the same physical local frame used by controls, gun rays and camera.
 * u/v offsets are metres, never a renderer-only displacement of the pilot. */
export function skyLeviathanRouteFrame(hullFrame, s, u, v) {
  const route = skyLeviathanInteriorPath(s);
  const right = p(-route.tangent.z, 0, route.tangent.x);
  const point = p(
    route.position.x + right.x * u,
    route.position.y + v,
    route.position.z + right.z * u,
  );
  return skyFlightFrameFromTangent(
    skyFlightPointToWorld(hullFrame, point),
    skyFlightVectorToWorld(hullFrame, route.tangent),
    skyFlightVectorToWorld(hullFrame, right),
  );
}

/** Navigation escorts share the hull's longitudinal travel and heading. Only
 * lateral/vertical offsets present a lens; a return never makes a dolphin swim
 * backward through the convoy. The same centres own visible/contact sockets. */
export function skyLeviathanComponentCenters(encounter, age) {
  return Object.fromEntries(
    encounter.geometry.components.map((part, index) => {
      const point = { ...part.center };
      if (part.kind === 'navigation') {
        const beat = skyLeviathanNavigationBeat(encounter, age);
        const near = beat?.componentId === part.id ? beat.fraction : 0;
        const side = Math.sign(part.center.x);
        point.x += (side * 2.1 - point.x) * near;
        // Present the raised cap below the mid-lane so its real front-facing
        // bezel stays readable even from the lowest permitted dodge altitude.
        point.y += (-0.7 - point.y) * near;
        // All escorts remain physically ahead of the nose. The held lens is
        // steady; swim continues in the actual rig without wobbling the socket.
        point.x += Math.sin(age * 0.5 + index) * 0.15 * (1 - near);
        point.y += Math.sin(age * 0.65 + index) * 0.12 * (1 - near);
      } else if (part.kind === 'fin') point.y += Math.sin(age * 0.8) * 0.25;
      return [part.id, point];
    }),
  );
}

/** Actual rigid joint rotations, XYZ radians after each authored rest basis.
 * Rendering and armor contacts share these values, including quiet-mode swim
 * suppression and the physical folded-fin crash pose. */
export function skyLeviathanArticulation(encounter, systems, reduced = false) {
  const age = encounter?.age ?? 0,
    crash = Math.max(0, Math.min(1, systems?.crashPose ?? 0));
  const pose = {
    hull: { x: 0, y: 0, z: 0 },
    jaw: { x: SKY_LEVIATHAN_JAW.openRadians * (systems?.jaw ?? 0), y: 0, z: 0 },
  };
  for (const [index, id] of ['tail_01', 'tail_02', 'tail_03'].entries())
    pose[id] = {
      x: reduced
        ? 0
        : Math.sin(age * 0.65 - index * 0.55) * [0.035, 0.07, 0.12][index] * (1 - crash),
      y: skyLeviathanTailBeat(encounter).angle * [1, 0.5, 0.3][index],
      z: 0,
    };
  for (const side of ['port', 'starboard']) {
    const lift = systems?.[side === 'port' ? 'portLift' : 'starboardLift'] ?? 1;
    pose[`fin_${side}`] = {
      x: 0,
      y: 0,
      z:
        (side === 'port' ? -1 : 1) *
        (((reduced ? 0 : Math.sin(age * 0.8) * 0.09) * lift + (1 - lift) * 0.35) * (1 - crash) +
          Math.abs(
            side === 'port' ? assembly.crash.finPortRadians : assembly.crash.finStarboardRadians,
          ) *
            crash),
    };
  }
  return pose;
}

/** A visible1.4s backswing warns before the1.4s physical tail strike.
 * Recovery returns to neutral before repeating; reduced motion retains this
 * gameplay movement because its actual facets own contact, not a hidden box. */
export function skyLeviathanTailBeat(encounter) {
  if (encounter?.phase !== 'wound') return { charge: 0, active: false, angle: 0 };
  const clock = encounter.phaseAge % 10;
  const smooth = (v) => {
    const t = Math.max(0, Math.min(1, v));
    return t * t * t * (10 - 15 * t + 6 * t * t);
  };
  return {
    charge: clock < 1.4 ? clock / 1.4 : 0,
    active: clock >= 1.4 && clock < 2.8,
    angle:
      clock < 1.4
        ? -0.12 * smooth(clock / 1.4)
        : clock < 2.8
          ? -0.12 + 0.37 * smooth((clock - 1.4) / 1.4)
          : 0.25 * (1 - smooth((clock - 2.8) / 1.2)),
  };
}

/** Measured exposed upper-lip mounts, in hull-local metres/radians. The
 * forward mechanical stops keep both barrels outside the sculpted skull. */
export const SKY_LEVIATHAN_CANNON_MOUNTS = Object.freeze(
  ['port', 'starboard'].map((side) =>
    Object.freeze({
      side,
      base: Object.freeze(p(side === 'port' ? -7 : 7, 5, 30.2)),
      restForward: Object.freeze(p(0, 0, 1)),
      length: 1.7,
      yawLimit: (70 * Math.PI) / 180,
      pitchLimit: (50 * Math.PI) / 180,
    }),
  ),
);

/** Measured 1.7m barrels gimbal about their fixed hull mounts. Actual muzzle
 * origins, aim direction and renderer pivot placement share this solution. */
export function skyLeviathanCannons(hull, targets) {
  return SKY_LEVIATHAN_CANNON_MOUNTS.map(({ side, base: mount, length, yawLimit, pitchLimit }) => {
    const base = skyFlightPointToWorld(hull, mount),
      target = skyFlightPointToLocal(hull, targets[side]);
    const dx = target.x - mount.x,
      dy = target.y - mount.y,
      dz = target.z - mount.z;
    const yaw = Math.atan2(dx, dz),
      pitch = Math.atan2(dy, Math.hypot(dx, dz));
    const y = Math.max(-yawLimit, Math.min(yawLimit, yaw)),
      a = Math.max(-pitchLimit, Math.min(pitchLimit, pitch));
    const direction = skyFlightVectorToWorld(
      hull,
      p(Math.sin(y) * Math.cos(a), Math.sin(a), Math.cos(y) * Math.cos(a)),
    );
    return {
      side,
      base,
      direction,
      canFire: Math.abs(yaw) <= yawLimit && Math.abs(pitch) <= pitchLimit,
      origin: p(
        base.x + direction.x * length,
        base.y + direction.y * length,
        base.z + direction.z * length,
      ),
    };
  });
}

/** Hull-local slab colliders match the hollow assembly. The pressure plug
 * physically retracts as rupture opens; it cannot vanish on a cosmetic flag.
 * Renderer/Blender assembly must consume these same positions and dimensions. */
export function skyLeviathanWalls(systems) {
  const walls = [],
    box = (id, center, halfExtents) => walls.push({ id, center, halfExtents });
  box('throat-left', p(-3.2, 0, 15), p(0.2, 2.9, 8));
  box('throat-right', p(3.2, 0, 15), p(0.2, 2.9, 8));
  box('throat-roof', p(0, 2.7, 15), p(3.4, 0.2, 8));
  box('throat-floor', p(0, -2.7, 15), p(3.4, 0.2, 8));
  for (let i = 0; i < 4; i++) {
    const z = 23.5 + i,
      halfWidth = 3 + i / 7,
      halfHeight = 2.5 + i / 7;
    for (const side of [-1, 1]) {
      box(
        `reducer-side-${side}-${i}`,
        p(side * (halfWidth + 0.2), 0, z),
        p(0.2, halfHeight + 0.4, 0.5),
      );
      box(
        `reducer-height-${side}-${i}`,
        p(0, side * (halfHeight + 0.2), z),
        p(halfWidth + 0.4, 0.2, 0.5),
      );
    }
  }
  box('chamber-port', p(-5.2, 0, 1), p(0.2, 4.9, 6));
  for (const side of [-1, 1]) box(`chamber-height-${side}`, p(0, side * 4.7, 1), p(5.4, 0.2, 6));
  box('chamber-rear', p(0, 0, -5.2), p(5.4, 4.9, 0.2));
  for (const side of [-1, 1]) {
    box(`chamber-entry-side-${side}`, p(side * 4.2, 0, 7.2), p(1.2, 4.9, 0.2));
    box(`chamber-entry-height-${side}`, p(0, side * 3.7, 7.2), p(3, 1.2, 0.2));
    box(`breach-height-${side}`, p(5.2, side * 3.7, 0), p(0.2, 0.2, 4.4));
    box(`breach-edge-${side}`, p(5.2, 0, side * 4.2), p(0.2, 3.5, 0.2));
    box(`chamber-starboard-height-${side}`, p(5.2, side * 4, 0), p(0.2, 0.5, 4));
  }
  box('chamber-starboard-rear', p(5.2, 0, -4.5), p(0.2, 4.5, 0.5));
  box('chamber-starboard-front', p(5.2, 0, 5.5), p(0.2, 4.5, 1.5));
  box('pressure-plug', p(5.2, 0, 8 * systems.breach), p(0.25, 3.5, 4));
  return walls;
}

/** Telegraphing sliding gates share visible aperture and physical box bounds.
 * The safe gap moves continuously;1.4s stationary preparation precedes each
 * crossing motion. Two offset cycles require actual route steering. */
export function skyLeviathanMachinery(age) {
  return [18, 27].map((s, index) => {
    const local = (age + index * 2) % 8;
    const side = Math.floor((age + index * 2) / 8) % 2 ? -1 : 1;
    const shift =
      local < 1.4 ? side * 0.65 : side * 0.65 * Math.cos(Math.min(1, (local - 1.4) / 2) * Math.PI);
    const gap = SPACE.machineryGapHalfWidth,
      leftEdge = shift - gap,
      rightEdge = shift + gap;
    return {
      id: `gate-${index}`,
      s,
      center: p(shift, 0, 38 - s),
      charge: local < 1.4 ? local / 1.4 : 0,
      gapCenter: shift,
      gapHalfWidth: gap,
      boxes: [
        {
          id: `gate-${index}-left`,
          center: p((-3 + leftEdge) / 2, 0, 38 - s),
          halfExtents: p((leftEdge + 3) / 2, 2.5, 0.3),
        },
        {
          id: `gate-${index}-right`,
          center: p((rightEdge + 3) / 2, 0, 38 - s),
          halfExtents: p((3 - rightEdge) / 2, 2.5, 0.3),
        },
      ],
    };
  });
}

/** Euclidean sphere-to-box contact in the same hull-local metres as walls. */
export function skyLeviathanBoxContact(point, radius, box) {
  return (
    Math.hypot(
      ...['x', 'y', 'z'].map((axis) =>
        Math.max(0, Math.abs(point[axis] - box.center[axis]) - box.halfExtents[axis]),
      ),
    ) < radius
  );
}

/** Earliest penetrating swept sphere/box contact, fraction0..1. Positions and
 * radius are hull-local metres. A moving/resizing slab interpolates its actual
 * previous and current faces; rounded sphere corners are retained rather than
 * treating an expanded rectangular box as a false collision volume. Tangent
 * sliding or departing a touching surface is allowed. Invalid input is null. */
export function skyLeviathanBoxSweep(from, to, radius, box, previous = box) {
  const axes = ['x', 'y', 'z'];
  if (
    !(Number.isFinite(radius) && radius > 0) ||
    !axes.every(
      (axis) =>
        [
          from?.[axis],
          to?.[axis],
          box?.center?.[axis],
          previous?.center?.[axis],
          box?.halfExtents?.[axis],
          previous?.halfExtents?.[axis],
        ].every(Number.isFinite) &&
        box.halfExtents[axis] > 0 &&
        previous.halfExtents[axis] > 0,
    )
  )
    return null;
  // Most tunnel faces are metres away. Reject their swept bounding intervals
  // before allocating the small exact piecewise-quadratic contact problem.
  for (const axis of axes) {
    const low = Math.min(
      previous.center[axis] - previous.halfExtents[axis],
      box.center[axis] - box.halfExtents[axis],
    );
    const high = Math.max(
      previous.center[axis] + previous.halfExtents[axis],
      box.center[axis] + box.halfExtents[axis],
    );
    if (
      Math.min(from[axis], to[axis]) - radius >= high ||
      Math.max(from[axis], to[axis]) + radius <= low
    )
      return null;
  }
  const cuts = [0, 1];
  const faces = axes.map((axis) => {
    const offset = from[axis] - previous.center[axis];
    const delta = to[axis] - from[axis] - box.center[axis] + previous.center[axis];
    const size = previous.halfExtents[axis];
    const growth = box.halfExtents[axis] - size;
    return [
      [-offset - size, -delta - growth],
      [offset - size, delta - growth],
    ];
  });
  for (const pair of faces)
    for (const [a, b] of pair) {
      if (Math.abs(b) < 1e-14) continue;
      const t = -a / b;
      if (t > 0 && t < 1) cuts.push(t);
    }
  cuts.sort((a, b) => a - b);
  for (let i = 1; i < cuts.length; i++) {
    const low = cuts[i - 1],
      high = cuts[i];
    if (high - low < 1e-12) continue;
    const middle = (low + high) / 2;
    let a = 0,
      b = 0,
      c = -radius * radius;
    for (const pair of faces) {
      const face = pair.find(([offset, slope]) => offset + slope * middle > 0);
      if (!face) continue;
      a += face[1] ** 2;
      b += 2 * face[0] * face[1];
      c += face[0] ** 2;
    }
    const value = (a * low + b) * low + c;
    let fraction = null;
    if (value < -1e-10 || (Math.abs(value) <= 1e-10 && 2 * a * low + b < -1e-10)) fraction = low;
    else if (a > 1e-14) {
      const discriminant = b * b - 4 * a * c;
      if (discriminant > 1e-12) {
        const enter = (-b - Math.sqrt(discriminant)) / (2 * a);
        const exit = (-b + Math.sqrt(discriminant)) / (2 * a);
        if (enter < high - 1e-10 && exit > low + 1e-10) fraction = Math.max(low, enter);
      }
    } else if (b < -1e-14) {
      const enter = -c / b;
      if (enter < high - 1e-10 && enter >= low) fraction = enter;
    }
    if (fraction !== null)
      return {
        fraction,
        point: Object.fromEntries(
          axes.map((axis) => [axis, from[axis] + (to[axis] - from[axis]) * fraction]),
        ),
        boxId: box.id,
      };
  }
  return null;
}
